// --- Helper Functions ---

import { isVisitor } from "./help/user/user";

/**
 * Pads a customer ID with leading zeros to ensure it's at least 5 characters.
 */
const padCustomerId = (customerId: string | undefined): string => {
  if (!customerId) {
    return "undefined";
  }
  const str = String(customerId);
  if (str.length >= 5) {
    return str;
  }
  const paddingNeeded = 5 - str.length;

  return "0".repeat(paddingNeeded) + str;
};

/**
 * Formats the user ID based on whether they are a visitor or not.
 */
const getFormattedUserId = (userId: string): string => {
  const paddedId = padCustomerId(userId);

  return isVisitor() ? `Visitor-${paddedId}` : paddedId;
};

// --- Amplitude Wrapper Functions ---

/**
 * Safely sets the user ID in Amplitude.
 */
export const setAmplitudeUserId = (userId: string) => {
  if (typeof window !== "undefined" && window.amplitude) {
    try {
      const formattedUserId = getFormattedUserId(userId);
      console.log("Setting Amplitude user ID:", formattedUserId);
      window.amplitude.setUserId(formattedUserId);
    } catch (error) {
      console.error("Failed to set Amplitude user ID:", error);
    }
  }
};

/**
 * Safely tracks an event in Amplitude.
 */
export const trackAmplitudeEvent = (
  eventName: string,
  properties?: Record<string, any>
) => {
  if (typeof window !== "undefined" && window.amplitude) {
    try {
      window.amplitude.track(eventName, properties);
    } catch (error) {
      console.error(`Failed to track Amplitude event "${eventName}":`, error);
    }
  }
};

/**
 * Safely sets user properties using Amplitude's Identify API.
 */
export const setAmplitudeIdentify = (identifyData: Record<string, any>) => {
  if (typeof window !== "undefined" && window.amplitude) {
    try {
      const identify = new window.amplitude.Identify();
      Object.entries(identifyData).forEach(([key, value]) => {
        identify.set(key, value);
      });
      window.amplitude.identify(identify);
    } catch (error) {
      console.error("Failed to set Amplitude identify properties:", error);
    }
  }
};

/**
 * Safely resets Amplitude (clears user ID and device ID).
 * Call this on user logout.
 */
export const resetAmplitude = () => {
  if (typeof window !== "undefined" && window.amplitude) {
    try {
      window.amplitude.reset();
      console.log("Amplitude instance reset.");
    } catch (error) {
      console.error("Failed to reset Amplitude:", error);
    }
  }
};
